import { downloadArtworkRequestImage } from "@hops/sdk";
import {
  assignArtistMutation,
  getArtworkRequestQueryKey,
  listArtworkRequestsInfiniteQueryKey,
  listArtworkRequestsQueryKey,
  updateArtworkStatusMutation,
} from "@hops/sdk/react-query";
import type { ArtworkRequestDetailResponse } from "@hops/sdk/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useAuthStore } from "../../auth/auth-store";
import type { IAdobeHost } from "../../host/types";
import { useTimerStore } from "../../timer/timer-store";

interface UseStartWorkOptions {
  host: IAdobeHost;
}

export function useStartWork({ host }: UseStartWorkOptions) {
  const queryClient = useQueryClient();
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const assignMutation = useMutation(assignArtistMutation());
  const statusMutation = useMutation(updateArtworkStatusMutation());

  const startWork = useCallback(
    async (request: ArtworkRequestDetailResponse) => {
      setIsStarting(true);
      setError(null);

      try {
        const userId = useAuthStore.getState().user?.id;
        const requestId = Number(request.id);

        // 1. Assign to current user (if not already assigned)
        if (userId && String(request.assignedToUserId) !== String(userId)) {
          await assignMutation.mutateAsync({
            path: { id: requestId },
            body: { userId },
          });
        }

        // 2. Set status to INPR
        if (request.statusCode !== "INPR") {
          await statusMutation.mutateAsync({
            path: { id: requestId },
            body: {
              statusCode: "INPR",
              note: `Started from ${host.appName === "photoshop" ? "Photoshop" : "Illustrator"} panel`,
              attachmentUrl: null,
              attachmentThumb: null,
              sendEmail: null,
            },
          });
        }

        // 3. Download reference images to temp directory
        const tempDir = await host.getTempDirectory();
        const imagePaths: string[] = [];

        if (request.images && request.images.length > 0) {
          for (const img of request.images) {
            try {
              const { data } = await downloadArtworkRequestImage({
                path: { id: requestId, imageId: Number(img.id) },
              });

              if (data) {
                // data is a Blob from the file download
                const arrayBuffer =
                  data instanceof Blob
                    ? await data.arrayBuffer()
                    : (data as ArrayBuffer);

                const extension =
                  img.customerImageName?.split(".").pop() ?? "png";
                const filename = `ref_${img.id}.${extension}`;
                const filePath = `${tempDir}/${filename}`;

                // Write file to disk through host (platform-specific)
                await host.writeFile(filePath, arrayBuffer);
                imagePaths.push(filePath);
              }
            } catch {
              // Non-fatal: skip images that fail to download
              console.warn(`Failed to download image ${img.id}`);
            }
          }
        }

        // 4. Create document
        await host.createDocument({
          width: 6,
          height: 6,
          dpi: 300,
          name: request.requestId ?? `artwork_${request.id}`,
        });

        // 5. Place reference images
        for (const path of imagePaths) {
          try {
            await host.placeImage(path);
          } catch {
            console.warn(`Failed to place image: ${path}`);
          }
        }

        // 6. Start timer
        useTimerStore.getState().start(request.requestId ?? String(request.id));

        // 7. Invalidate queries
        queryClient.invalidateQueries({
          queryKey: listArtworkRequestsQueryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: listArtworkRequestsInfiniteQueryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: getArtworkRequestQueryKey({ path: { id: requestId } }),
        });

        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to start work");
        return false;
      } finally {
        setIsStarting(false);
      }
    },
    [host, assignMutation, statusMutation, queryClient],
  );

  return {
    startWork,
    isStarting,
    error,
  };
}
